import React from 'react';
import KPICard from '../ui/KPICard';
import Card from '../ui/Card';
import Section from '../ui/Section';
import Badge from '../ui/Badge';
import { formatCurrency, formatNumber } from '../dashboardUtils';
import {
    useSalesOverviewQuery,
    useOrderStatusQuery,
    useSalesTrendQuery,
} from '../../../../api/sales.api';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
} from 'recharts';
import { Link } from 'react-router-dom';

/**
 * Sales Analytics View Component
 */
const SalesAnalyticsView = ({ dateRange }) => {
    const { from, to } = dateRange;

    const { data: salesOverviewData, isLoading: salesLoading } = useSalesOverviewQuery({ from, to });
    const { data: orderStatusData, isLoading: orderLoading } = useOrderStatusQuery({ from, to });
    const { data: salesTrendData, isLoading: trendLoading } = useSalesTrendQuery({ from, to });

    const salesOverview = salesOverviewData?.result || {};
    const orderStatus = orderStatusData?.result || {};
    const salesTrend = salesTrendData?.result || {};

    // Backend returns two different shapes:
    // - With date range:    { customPeriod: { totalRevenue, totalProfit, ... } }
    // - Without date range: { today, yesterday, thisWeek, thisMonth, growth }
    //
    // Build a chart-friendly array from whichever shape we got.
    const trendPoints = (() => {
        if (salesTrend.customPeriod) {
            // Single period summary → show as one bar/point
            const p = salesTrend.customPeriod;
            return [{ date: `${p.from || 'From'} → ${p.to || 'To'}`, revenue: p.totalRevenue || 0, profit: p.totalProfit || 0 }];
        }
        if (salesTrend.today || salesTrend.thisWeek || salesTrend.thisMonth) {
            // Period summary mode — build a 4-point sparkline
            return [
                { date: 'Yesterday', revenue: salesTrend.yesterday?.totalRevenue || 0, profit: salesTrend.yesterday?.totalProfit || 0 },
                { date: 'Today',     revenue: salesTrend.today?.totalRevenue     || 0, profit: salesTrend.today?.totalProfit     || 0 },
                { date: 'This Week', revenue: salesTrend.thisWeek?.totalRevenue  || 0, profit: salesTrend.thisWeek?.totalProfit  || 0 },
                { date: 'This Month',revenue: salesTrend.thisMonth?.totalRevenue || 0, profit: salesTrend.thisMonth?.totalProfit || 0 },
            ];
        }
        return [];
    })();

    const orderCards = [
        { label: 'Pending Orders', count: orderStatus.pending || 0, color: 'bg-amber-50 text-amber-900 border-amber-200' },
        { label: 'Shipped Orders', count: orderStatus.shipped || 0, color: 'bg-sky-50 text-sky-900 border-sky-200' },
        { label: 'Delivered Orders', count: orderStatus.delivered || 0, color: 'bg-emerald-50 text-emerald-900 border-emerald-200' },
        { label: 'Cancelled Orders', count: orderStatus.canceled || 0, color: 'bg-rose-50 text-rose-900 border-rose-200' },
    ];

    return (
        <div className="space-y-8 w-full min-w-0">
            {/* Sales KPI Cards */}
            <Section title="Sales Overview" subtitle="Detailed sales breakdown and transaction performance">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 w-full min-w-0">
                    <KPICard
                        label="Total Revenue"
                        value={formatCurrency(salesOverview.totalRevenue)}
                        loading={salesLoading}
                        accentClass="bg-stone-900 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Total Profit"
                        value={formatCurrency(salesOverview.totalProfit)}
                        loading={salesLoading}
                        accentClass="bg-emerald-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Avg Order Value"
                        value={formatCurrency(salesOverview.avgOrderValue)}
                        loading={salesLoading}
                        accentClass="bg-indigo-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 11h14l1 12H4L5 11z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Total Cost"
                        value={formatCurrency(salesOverview.totalCost)}
                        loading={salesLoading}
                        accentClass="bg-stone-700 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Units Sold"
                        value={formatNumber(salesOverview.totalUnitsSold)}
                        loading={salesLoading}
                        accentClass="bg-teal-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        }
                    />
                </div>
            </Section>

            {/* Order Fulfillment Status Section */}
            <Section
                title="Order Fulfillment Status"
                subtitle="Live status counts for active orders"
                action={
                    <Link
                        to="/dashboard/order_list"
                        className="text-xs font-semibold text-stone-900 hover:underline flex items-center gap-1"
                    >
                        View Orders List →
                    </Link>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full min-w-0">
                    {orderCards.map((item, idx) => (
                        <div
                            key={idx}
                            className={`p-5 rounded-2xl border ${item.color} shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow min-w-0`}
                        >
                            <p className="text-xs font-semibold uppercase tracking-wider opacity-75 truncate">
                                {item.label}
                            </p>
                            <div className="flex items-baseline justify-between mt-3 min-w-0">
                                <span className="text-3xl font-bold tracking-tight truncate">
                                    {orderLoading ? '...' : item.count}
                                </span>
                                <Badge variant="neutral" size="xs">
                                    Active
                                </Badge>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Sales Trend Chart & Comparisons */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 w-full min-w-0">
                {/* Recharts Area Chart */}
                <div className="lg:col-span-2 min-w-0">
                    <Section title="Revenue vs Profit Trend" subtitle="Area analysis over current filtered range">
                        <Card noPadding>
                            <div className="p-6 w-full min-w-0">
                                {trendLoading ? (
                                    <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                        Loading sales trend chart...
                                    </div>
                                ) : trendPoints.length === 0 ? (
                                    <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                        No sales trend data for selected range
                                    </div>
                                ) : (
                                    <div className="h-80 w-full min-w-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart
                                                data={trendPoints}
                                                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                            >
                                                <defs>
                                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#0f172a" stopOpacity={0.8}/>
                                                        <stop offset="95%" stopColor="#0f172a" stopOpacity={0.0}/>
                                                    </linearGradient>
                                                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                                                <YAxis stroke="#94a3b8" fontSize={11} />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#0f172a',
                                                        borderRadius: '0.75rem',
                                                        color: '#f8fafc',
                                                        fontSize: '12px',
                                                    }}
                                                    formatter={(val) => [formatCurrency(val), '']}
                                                />
                                                <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: '15px', fontSize: '12px' }} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="revenue"
                                                    name="Revenue"
                                                    stroke="#0f172a"
                                                    fillOpacity={1}
                                                    fill="url(#colorRevenue)"
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="profit"
                                                    name="Profit"
                                                    stroke="#10b981"
                                                    fillOpacity={1}
                                                    fill="url(#colorProfit)"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Section>
                </div>

                    <Section title="Comparative Performance" subtitle="Daily & period milestones">
                        <div className="space-y-4 w-full min-w-0">

                            {/* Show custom period summary when date range is selected */}
                            {salesTrend.customPeriod ? (
                                <Card title="Selected Period Summary">
                                    <div className="space-y-3 text-xs">
                                        <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                            <span className="text-stone-500">Period:</span>
                                            <span className="font-semibold text-stone-700">
                                                {salesTrend.customPeriod.from} → {salesTrend.customPeriod.to}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                            <span className="text-stone-500">Revenue:</span>
                                            <span className="font-bold text-stone-900">
                                                {formatCurrency(salesTrend.customPeriod.totalRevenue)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                            <span className="text-stone-500">Profit:</span>
                                            <span className="font-bold text-emerald-700">
                                                {formatCurrency(salesTrend.customPeriod.totalProfit)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                            <span className="text-stone-500">Total Orders:</span>
                                            <span className="font-bold text-stone-900">
                                                {formatNumber(salesTrend.customPeriod.totalOrders)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-1">
                                            <span className="text-stone-500">Units Sold:</span>
                                            <span className="font-bold text-stone-900">
                                                {formatNumber(salesTrend.customPeriod.totalUnitsSold)}
                                            </span>
                                        </div>
                                    </div>
                                </Card>
                            ) : (
                                <>
                                    {/* Today vs Yesterday */}
                                    <Card title="Daily Comparison">
                                        <div className="space-y-3 text-xs">
                                            <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                                <span className="text-stone-500">Today Revenue:</span>
                                                <span className="font-bold text-stone-900">
                                                    {formatCurrency(salesTrend.today?.totalRevenue)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                                <span className="text-stone-500">Yesterday:</span>
                                                <span className="font-bold text-stone-900">
                                                    {formatCurrency(salesTrend.yesterday?.totalRevenue)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-1">
                                                <span className="text-stone-500">Daily Growth:</span>
                                                <Badge
                                                    variant={
                                                        (salesTrend.growth?.dailyRevenueDifference || 0) >= 0
                                                            ? 'success'
                                                            : 'danger'
                                                    }
                                                >
                                                    {salesTrend.growth?.dailyGrowthPercentage || '0%'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Card>

                                    {/* This Week */}
                                    <Card title="This Week Summary">
                                        <div className="space-y-3 text-xs">
                                            <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                                <span className="text-stone-500">Revenue:</span>
                                                <span className="font-bold text-stone-900">
                                                    {formatCurrency(salesTrend.thisWeek?.totalRevenue)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-1">
                                                <span className="text-stone-500">Total Orders:</span>
                                                <span className="font-bold text-stone-900">
                                                    {formatNumber(salesTrend.thisWeek?.totalOrders)}
                                                </span>
                                            </div>
                                        </div>
                                    </Card>

                                    {/* This Month */}
                                    <Card title="This Month Summary">
                                        <div className="space-y-3 text-xs">
                                            <div className="flex justify-between items-center py-1 border-b border-stone-100">
                                                <span className="text-stone-500">Revenue:</span>
                                                <span className="font-bold text-stone-900">
                                                    {formatCurrency(salesTrend.thisMonth?.totalRevenue)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-1">
                                                <span className="text-stone-500">Units Sold:</span>
                                                <span className="font-bold text-stone-900">
                                                    {formatNumber(salesTrend.thisMonth?.totalUnitsSold)}
                                                </span>
                                            </div>
                                        </div>
                                    </Card>
                                </>
                            )}
                        </div>
                    </Section>
            </div>
        </div>
    );
};

export default SalesAnalyticsView;
